import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  AppBar,
  Toolbar,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './components/LoginPage';
import { UnauthorizedPage } from './components/UnauthorizedPage';
import { UserMenu } from './components/UserMenu';
import MultiStepForm from './components/MultiStepForm';
import VendorDashboard from './components/VendorDashboard';
import OrgChart from './components/OrgChart';
import DatabaseQuery from './components/DatabaseQuery';
import IroncladTest from './pages/IroncladTest';
import Nimbus from './pages/Nimbus';
import { NotificationProvider } from './components/NotificationContext';
import logo from './assets/Angel-Logo-White.webp';

const drawerWidth = 240;

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function AppContent() {
  const navigate = useNavigate();
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const isFinance = user?.departmentDesc === 'Finance and Accounting';
  
  console.log('[App] User:', user);
  console.log('[App] User role:', user?.role);
  console.log('[App] isAdmin:', isAdmin);
  console.log('[App] isSuperAdmin:', isSuperAdmin);
  console.log('[App] User departmentDesc:', user?.departmentDesc);
  console.log('[App] isFinance:', isFinance);

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#1a1a1a',
        }}
      >
        <Toolbar>
          <img
            src={logo}
            alt="Angel Studios"
            style={{ height: '40px', marginRight: '24px' }}
          />
          <Typography
            variant="h5"
            component="h1"
            sx={{
              flexGrow: 1,
              textAlign: 'center',
              fontWeight: 600,
            }}
          >
            FinOps Portal
          </Typography>
          <UserMenu />
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <Typography
            variant="h6"
            sx={{ px: 2, py: 2, fontWeight: 600, color: 'primary.main' }}
          >
            FinOps
          </Typography>
          <Divider />
          <List>
            {/* Everyone sees Dashboard */}
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/')}>
                <ListItemText primary="Vendor Due Diligence" />
              </ListItemButton>
            </ListItem>
            
            {/* Everyone sees Org Chart */}
            <ListItem disablePadding>
              <ListItemButton onClick={() => navigate('/org-chart')}>
                <ListItemText primary="Org Chart" />
              </ListItemButton>
            </ListItem>
            
            {/* Super Admin, Admin, and Finance see NIMBUS */}
            {(isSuperAdmin || isAdmin || isFinance) && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => navigate('/nimbus')}>
                  <ListItemText primary="NIMBUS" />
                </ListItemButton>
              </ListItem>
            )}
            
            {/* Only Super Admin sees Ironclad Test */}
            {isSuperAdmin && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => navigate('/ironclad-test')}>
                  <ListItemText primary="🧪 Ironclad Test" />
                </ListItemButton>
              </ListItem>
            )}
            
            {/* Admin Tools: All admins can access (page-level permissions still apply) */}
            {isAdmin && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => navigate('/database-query')}>
                  <ListItemText primary="Admin" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          backgroundColor: '#f5f5f5',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <VendorDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor-due-diligence/new"
            element={
              <ProtectedRoute>
                <MultiStepForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor-due-diligence/:id"
            element={
              <ProtectedRoute>
                <MultiStepForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/form/:id"
            element={
              <ProtectedRoute>
                <MultiStepForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/org-chart"
            element={
              <ProtectedRoute>
                <OrgChart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ironclad-test"
            element={
              <ProtectedRoute>
                <IroncladTest />
              </ProtectedRoute>
            }
          />
          <Route
            path="/database-query"
            element={
              <ProtectedRoute>
                <DatabaseQuery />
              </ProtectedRoute>
            }
          />
          <Route
            path="/nimbus"
            element={
              <ProtectedRoute>
                <Nimbus />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <NotificationProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </NotificationProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
